import React, { useEffect } from 'react';
import { Box, Container, Paper, Typography, Divider } from '@mui/material';
import { TokenResponse } from '@react-oauth/google';

import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { useUser } from '../../context/UserContext';

const PrivacyPolicyPage = () => {
    const { login, isLoading } = useUser();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleSuccess = async (tokenResponse: TokenResponse) => {
        try {
            await login(tokenResponse);
        } catch (loginError) {
            console.error(loginError);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Header onLoginSuccess={handleSuccess} isLoading={isLoading} />

            <Container component="main" maxWidth="md" sx={{ flexGrow: 1, py: 4 }}>
                <Paper sx={{ p: { xs: 3, md: 5 } }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Privacy Policy
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        1. Introduction
                    </Typography>
                    <Typography paragraph>
                        ISN Group d.o.o. ("we", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share your personal data when you use Ecom.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        2. Data Controller
                    </Typography>
                    <Typography paragraph>
                        <strong>ISN Group d.o.o.</strong><br />
                        Dunajska cesta 159, 1000 Ljubljana, Slovenia<br />
                        Email: <a href="mailto:support@ecom.time2future.com" style={{ color: 'inherit' }}>support@ecom.time2future.com</a>
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        3. Data We Collect
                    </Typography>
                    <ul>
                        <li><strong>Account Data:</strong> Email address, name, and password (hashed) or OAuth tokens.</li>
                        <li><strong>Usage Data:</strong> Logs of how you use the service, IP address, browser type.</li>
                        <li><strong>User Content:</strong> Text prompts, original images uploaded for processing, and the resulting AI-generated content.</li>
                        <li><strong>Transaction Data:</strong> Purchase history (Note: We do not store credit card details; these are handled securely by our Merchant of Record, Paddle.com).</li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        4. How We Use Your Data
                    </Typography>
                    <Typography paragraph>
                        We use your data to provide and maintain the AI generation service, manage your credits, comply with legal obligations (Slovenian tax laws), and improve our service quality.
                    </Typography>
                    <Typography paragraph sx={{ fontStyle: 'italic', backgroundColor: 'rgba(255,255,255,0.05)', p: 1, borderRadius: 1 }}>
                        <strong>AI Training Note:</strong> We do not use your private User Content (uploaded images or specific prompts) to train public AI models. Your inputs are processed strictly to generate your requested results.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        5. Data Sharing and Processors
                    </Typography>
                    <Typography paragraph>
                        We share data with the following third-party service providers strictly for the purpose of operating our service:
                    </Typography>
                    <ul>
                        <li><strong>Paddle.com:</strong> Payment processing and Merchant of Record.</li>
                        <li><strong>Google Analytics:</strong> Website traffic analysis (anonymized).</li>
                        <li><strong>Cloudinary:</strong> Hosting and processing of images.</li>
                        <li><strong>AI Providers:</strong> We send your prompts and inputs to third-party LLM providers (such as OpenAI, Gemini, Perplexity) solely to generate results.</li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        6. Your Rights (GDPR) & Data Retention
                    </Typography>
                    <Typography paragraph>
                        Under the GDPR, you have the right to access, rectify, export, restrict processing, or request the deletion of your personal data (<strong>"Right to be Forgotten"</strong>). To exercise these rights, please contact us at <a href="mailto:support@ecom.time2future.com" style={{ color: 'inherit' }}>support@ecom.time2future.com</a>.
                    </Typography>
                    <Typography paragraph sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 2 }}>
                        <strong>Retention Exception:</strong> Please note that while we will delete your account and usage data upon request, we are legally required to retain **transaction records and invoices for a period of 10 years** to comply with Slovenian tax legislation (ZDavP-2). These records cannot be erased before the statutory period expires.
                    </Typography>
                </Paper>
            </Container>

            <Footer />
        </Box>
    );
};

export default PrivacyPolicyPage;
